import config from '../../config/index.json';
import { encode } from '../../utils/encode';
import utilAxios, { generateAxiosInstance } from '../../utils/axios';
import userActions from './Actions';
import errorHandler from '../../utils/interceptors/errorHandler';
import successHandler from '../../utils/interceptors/successHandler';
import getHeader from '../../utils/header';
import { getOktaSessionID, getIdToken, clearSecureTokens } from '../../utils/tokenManager';


const { VITE_S3_ENABLE } = import.meta.env;
const { VITE_API_URL } = import.meta.env;
const { VITE_REDIRECT_TO_OLD_URL, VITE_OKTA_DOMAIN, VITE_INTEGRATION_URL, VITE_OKTA_GLOBAL_DOMAIN, VITE_GLOBAL_OKTA_OAUTH, VITE_NODE_ENV} = import.meta.env;
const baseURL = {};
if (VITE_S3_ENABLE === 'true') {
  baseURL.baseURL = VITE_API_URL;
} else {
  baseURL.baseURL = '/';
}

const generateFormBody = () => {
  const userDetails = {
    username: '',
    password: '',
    grant_type: 'password',
    role: 'PUBLIC',
    scope: localStorage.getItem('StoreName') || 'resmed_us',
  };

  const userFormBody = [];
  Object.keys(userDetails).forEach((encodedKey) => {
    const encodedValue = userDetails[encodedKey];
    userFormBody.push(`${encodedKey}=${encodedValue}`);
  });
  return userFormBody.join('&');
};

const axios1 = generateAxiosInstance({
  baseURL: baseURL.baseURL,
  headers: getHeader({ functionName: 'login.generateAxiosInstance1', values: ['application/x-www-form-urlencoded;charset=utf-8'] }),
});

export const getDynamicMenu = async (account) => {
  try {
    const resp = await utilAxios.get(`${config.api.path}/menus/${localStorage.getItem('StoreName')}/info`, {
      headers: getHeader({ functionName: 'login.getDynamicMenu', values: [account] }),
    });
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const getToken = async () => {
  try {
    const resp = await axios1.post(`${config.api.path}/oauth2/tokens`, generateFormBody());
    return resp;
  } catch (err) {
    return err;
  }
};

const getTokenRemove = async () => {
  try {
    const resp = await utilAxios.delete(`${config.api.path}/oauth2/tokens`, {
      headers: getHeader({ functionName: 'login.getTokenRemove' }),
    });
    return resp;
  } catch (err) {
    return err;
  }
};

const logoutFromOkta = async (id) => {
  try {
    const resp = await axios1.delete(`${config.api.path}/extregistrations/${localStorage.getItem('StoreName')}/user/logout/${encode(id)}`, {
      headers: getHeader({ functionName: 'login.logoutFromOkta' }),
    });
    return resp;
  } catch (err) { 
    return err; 
  }
};

function logout({ dispatch = null } = {}) {
  const performLogout = async () => {
    try {
      // Capture values BEFORE clearing storage
      const storeName = localStorage.getItem('StoreName');
      const isEU = storeName?.includes('RESMED_EU') || window.name === 'RESMED_EU';

      if (isEU) {
        // ========================================
        // EU REGION: Use OIDC logout with id-token
        // ========================================
        const idToken = getIdToken(); // Get from in-memory storage

        // Clear in-memory tokens
        clearSecureTokens();

        // Clear all storage
        localStorage.clear();
        sessionStorage.clear();

        // Build OIDC logout URL
        const oktaDomain = VITE_OKTA_GLOBAL_DOMAIN;
        const oauthPath = `oauth2/${VITE_GLOBAL_OKTA_OAUTH}/v1`;
        const redirectUri = VITE_REDIRECT_TO_OLD_URL;

        if (idToken) {
          // Use proper OIDC logout with id_token_hint
          const oktaLogoutUrl = `${oktaDomain}${oauthPath}/logout?id_token_hint=${idToken}&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
          window.location.href = oktaLogoutUrl;
        } else {
          // Fallback: redirect to old URL
          window.location.href = redirectUri;
        }

      } else {
        // ========================================
        // AMR/APAC REGIONS: Use old logout process
        // ========================================
        const oktaSessionData = `OKTA_SID=${getOktaSessionID()};USER_ID=${localStorage.getItem('userLoginUsername')}`;
        await logoutFromOkta(oktaSessionData);
        // Dispatch logout action if provided
      if (dispatch) {
        await dispatch(userActions.logout(null));
      }

      // Remove OAuth tokens from backend
      await getTokenRemove();
        // Clear all storage
        localStorage.clear();
        sessionStorage.clear();

        // Get Okta logout URL (old approach)
        const fromURI = (VITE_NODE_ENV === 'UAT' || VITE_NODE_ENV === 'production') ? window.location.origin : VITE_REDIRECT_TO_OLD_URL;
        const oktaLogoutUrl = `${VITE_OKTA_DOMAIN}login/signout?fromURI=${fromURI}`;

        // Redirect to Okta logout
        if (oktaLogoutUrl) {
          window.location.href = oktaLogoutUrl;
        } else {
          window.location.href = VITE_REDIRECT_TO_OLD_URL;
        }
      }

    } catch (error) {
      console.error('Logout error:', error);
      // Fallback logout - clear storage and redirect
      clearSecureTokens();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = VITE_REDIRECT_TO_OLD_URL;
    }
  };
  
  performLogout();
}

const axios = generateAxiosInstance({
  baseURL: baseURL.baseURL,
  headers: getHeader({ functionName: 'login.generateAxiosInstance', values: ['application/json'] }),
});

axios.interceptors.response.use(
  response => successHandler(response),
  error => errorHandler(error),
);

const login = async (username, password) => {
  try {
    const resp = await axios.post(`${VITE_INTEGRATION_URL}integration/api/auth/rosadmin`, { username, password });
    return resp;
  } catch (err) {
    throw err.message;
  }
};


const getStoreName = async (user) => {
  try {
    const resp = await axios.post(`${VITE_INTEGRATION_URL}integration/api/auth/userstore`, { userid: user }, {
      headers: getHeader({ functionName: 'login.getStoreName', values: ['application/json', `${VITE_API_URL}cortex`] }),
    });
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const getUserIdFromOkta = async () => {
  const ax = generateAxiosInstance({
    baseURL: baseURL.baseURL,
    withCredentials: true,
  });
  try {
    const resp = await ax.get(`${localStorage.getItem('StoreName')?.includes('RESMED_EU') || window.name === 'RESMED_EU' ? VITE_OKTA_GLOBAL_DOMAIN : VITE_OKTA_DOMAIN}api/v1/sessions/me`, {
      headers: getHeader({ functionName: 'login.getUserIdFromOkta', values: ['application/json'] }),
    });
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const defaultprofile = async () => {
  const axiosCall = generateAxiosInstance({
    baseURL: baseURL.baseURL,
    headers: getHeader({ functionName: 'login.defaultprofile', values: ['application/json'] }),
  });
  try {
    const resp = await axiosCall.get(`/${config.api.path}?zoom=defaultprofile`);
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const defaultprofileWithAccount = async () => {
  const axiosCall = generateAxiosInstance({
    baseURL: baseURL.baseURL,
    headers: getHeader({ functionName: 'login.defaultprofileWithAccount', values: ['application/json'] }),
  });
  try {
    const resp = await axiosCall.get(`/${config.api.path}?zoom=defaultprofile,defaultprofile:accounts:element,defaultprofile:emails:element`);
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const myCompanyPermission = async (encodeValue) => {
  const axiosCall = generateAxiosInstance({
    baseURL: baseURL.baseURL,
    headers: getHeader({ functionName: 'login.myCompanyPermission', values: ['application/json'] }),
  });
  try {
    const resp = await axiosCall.get(`/${config.api.path}/rosutils/${localStorage.getItem('StoreName')}/${encodeValue}/info`);
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const sleepLabPermission = async (encodeValue) => {
  const axiosCall = generateAxiosInstance({
    baseURL: baseURL.baseURL,
    headers: getHeader({ functionName: 'login.sleepLabPermission', values: ['application/json'] }),
  });
  try {
    const resp = await axiosCall.get(`/${config.api.path}/rosutils/${localStorage.getItem('StoreName')}/${encodeValue}/info`);
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const dealerLocatorPermission = async (encodeValue) => {
  const axiosCall = generateAxiosInstance({
    baseURL: baseURL.baseURL,
    headers: getHeader({ functionName: 'login.dealerLocatorPermission', values: ['application/json'] }),
  });
  try {
    const resp = await axiosCall.get(`/${config.api.path}/rosutils/${localStorage.getItem('StoreName')}/${encodeValue}/info`);
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const getRequestTypes = async () => {
  const axiosCall = generateAxiosInstance({
    baseURL: baseURL.baseURL,
    headers: getHeader({ functionName: 'login.getRequestTypes', values: ['application/json'] }),
  });
  try {
    const resp = await axiosCall.get(`/${config.api.path}/ordertypes/${localStorage.getItem('StoreName')}/`);
    return resp;
  } catch (err) {
    throw err.message;
  }
};
const getUserList = async (id, pageNumber, rowsperPage) => {
  const acountId = id;
  const headerParams = {
    headers: getHeader({ functionName: 'login.getUserList' }),
  };
  try {
    const resp = await axios.get(`${config.api.path}/extregistrations/${localStorage.getItem('StoreName')}/user/account/${encode(acountId)}/1/1000`, headerParams);
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const getFilteredUsers = async (acountId, pageNumber, rowPerPage) => {
  const headerParams = {
    headers: getHeader({ functionName: 'login.getFilteredUsers' }),
  };
  const pageNo = pageNumber + 1 || 1;
  const rowPPage = rowPerPage || 5;
  try {
    const response = await axios.get(`${config.api.path}/extregistrations/${localStorage.getItem('StoreName')}/user/account/${encode(acountId)}/${pageNo}/${rowPPage}`, headerParams);
    return response;
  } catch (error) {
    return error.response;
  }
};


const getAssociatedCompanyName = async (id, userData, storeName) => {
  const acountId = `ACCOUNT_NUMBER=${id}`;
  const headerParams = {
    headers: getHeader({ functionName: 'login.getAssociatedCompanyName', values: [`${userData.token_type} ${userData.access_token}`] }),
  };
  try {
    const resp = await axios.get(`${config.api.path}/accountdetails/${storeName}/account/${encode(acountId)}/1/2`, headerParams);
    return resp;
  } catch (err) {
    throw err.message;
  }
};
const getorgId = async (countrycode, userData) => {
  const headerParams = {
    headers: getHeader({ functionName: 'login.getorgId', values: [`${userData.token_type} ${userData.access_token}`] }),
  };
  try {
    const resp = await axios.post(`${config.api.path}/master/resmed_us/lookups/countrycode/form?followlocation&format=standardlinks,zoom.nodatalinks&`, { operatingunitids: [countrycode] }, headerParams);
    return resp;
  } catch (err) {
    throw err.message;
  }
};

const getRequestTypesBeforeLogin = async (userData, storeName) => {
  const headerParams = {
    headers: getHeader({ functionName: 'login.getRequestTypesBeforeLogin', values: [`${userData.token_type} ${userData.access_token}`] }),
  };
  try {
    const resp = await axios.get(`/${config.api.path}/ordertypes/${storeName}/`, headerParams);
    return resp;
  } catch (err) {
    throw err.message;
  }
};
export const createNewUserBeforLogin = payload => getToken().then(async (data) => {
  const headerParams = {
    headers: getHeader({ functionName: 'login.createNewUserBeforLogin', values: [`${data.data.token_type} ${data.data.access_token}`] }),
  };
  try {
    const resp = await axios.post(`${config.api.path}/extregistrations/resmed_us/user/create`, payload, headerParams);
    return resp;
  } catch (err) {
    throw err.message;
  }
});

export default {
  login,
  logout,
  getToken,
  defaultprofile,
  getUserIdFromOkta,
  getStoreName,
  getDynamicMenu,
  defaultprofileWithAccount,
  myCompanyPermission,
  sleepLabPermission,
  dealerLocatorPermission,
  getRequestTypes,
  getUserList,
  getAssociatedCompanyName,
  getorgId,
  getRequestTypesBeforeLogin,
  createNewUserBeforLogin,
  getFilteredUsers,
  getTokenRemove,
};
